import React, {useState, useEffect, useMemo} from 'react';
import { useHistory } from 'react-router-dom';
import { ServerFailed } from '../errors/serverFailed'
import { Theme } from '@material-ui/core/styles';

import { Typography, Grid, CardContent, Button, Card, CardActions, makeStyles } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { useAuthenticatedUser } from '../../utilities/useAuthenticatedUser';
import { upsellToPaid } from '../../utilities/Upsell';
import { HorizontalCentered } from '../../components/HorizontalCentered';
import { VerticalCenter } from '../../components/VerticalCenter';
import { useIsMobile } from '../../components/Mui3WidthProvider';
import { useLibrary, useLibraryAlert } from './LibraryPage';
import { routes } from '../../routes';
import { LibraryItemService } from '../../services/LibraryItemService';
import { PremiumSermon } from '../../entities/PremiumSermon';
import { PremiumSermonService } from '../../services/PremiumSermonService';
import { PremiumSermonTable } from './Views/Components/PremiumSermonTable';
import { PreviewPremiumContentDialog } from './Views/Components/Dialogs/PreviewPremiumContentDialog';
import { AddAssetAction } from '../../entities/AddAssetAction';

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        opacity: 1,
        transition: 'all 0.3s ease 0s',
        MozTransition: 'all 0.3s ease 0s',
        WebkitTransition: 'all 0.3s ease 0s',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.9,
            transition: 'all 0.3s ease 0s',
            MozTransition: 'all 0.3s ease 0s',
            WebkitTransition: 'all 0.3s ease 0s'
        }
    },
}));

export function PremiumSermonDetails(){
    const classes = useStyles();
    const [selectedSermon, setSelectedSermon] = useState<PremiumSermon | undefined>();
    const [serverFailed, setServerFailed] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [showPreview, setShowPreview] = useState<boolean>(false);

    const [previewAssetId, setPreviewAssetId] = useState<string | undefined>(undefined);
    const previewAsset = useMemo(() => selectedSermon?.assets.find(a => a.id === previewAssetId) , [previewAssetId, selectedSermon])
    
    const history = useHistory();
    const user = useAuthenticatedUser();
    const isMobile = useIsMobile();
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();

    useEffect(() => {
        async function loadSeries(){
            const id = context.activeFolderId;

            if(!id) return history.push(routes.app.premiumSermonListPage);

            setServerFailed(false);
            setIsDisabled(true);
            const response = await PremiumSermonService.get(+id);
            setIsDisabled(false);

            if(response.success) return setSelectedSermon(response.data);
            else return setServerFailed(true);
        } 

        loadSeries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const openPreview = (assetId:string) => {
        setPreviewAssetId(assetId);
        setShowPreview(true);
    }

    const closePreview = () => {
        setPreviewAssetId(undefined);
        setShowPreview(false)
    }

    const activateSermon = async () => {
        if(!selectedSermon){
            return
        }

        setIsDisabled(true);
        const response = await PremiumSermonService.activate(selectedSermon.id);
        setIsDisabled(false);
        
        if(response.error) return libraryAlert(`Error activating ${selectedSermon.name}.`)

        if(response.success) {
            setIsDisabled(true);
            const addedSermonContainer = await LibraryItemService.getItem(response.data);
            const addedAssets = await LibraryItemService.getChildren(response.data)
            setIsDisabled(false);

            if(addedSermonContainer.error) libraryAlert(addedSermonContainer.message);
            if(addedAssets.error) libraryAlert(addedAssets.message);

            if(addedAssets.success && addedSermonContainer.success){
                addedAssets.data.map(context.addLibraryItem);
                context.addLibraryItem(addedSermonContainer.data)
                libraryAlert(`The ${selectedSermon.name} has been added to your Library.`)
                history.push(routes.app.resolve.libraryFolder(response.data));
            }
        }
    }

    return (
        <div style={{height: "100%"}}>
            {!isMobile &&
                <Grid container style={{height: 45, borderBottom: "1px solid rgb(225, 225, 225)"}}>
                    <Grid container item md={8} lg={9} alignItems="center">
                        <Grid item>
                            <Typography onClick={() => history.goBack()} style={{ color: "#5f6368", fontSize: "18px", fontWeight: 500, fontFamily: "Roboto, Helvetica", cursor: "pointer"}}>
                                Premium Sermon
                            </Typography>
                        </Grid>
                        <Grid item>
                            <ChevronRightIcon style={{ color: "#5f6368", verticalAlign: "middle" }} />
                        </Grid>
                        <Grid item>
                            <Typography style={{ color: "#5f6368", fontSize: "18px", fontWeight: 500, fontFamily: "Roboto, Helvetica"}}>
                                {selectedSermon?.name}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            }
            <Grid container style={{height: isMobile ? "100%" : "calc(100% - 45px)"}}>
            { serverFailed
            ? 
                <Grid container style={{height: "100%"}}>
                    <VerticalCenter>
                        <HorizontalCentered>
                            <ServerFailed errorMessage="We're sorry, but we're having trouble loading Premium Sermons." />
                        </HorizontalCentered>
                    </VerticalCenter>
                </Grid>
            :
                <Grid item xs={12} style={{height: "100%", overflowY: "auto", overflowX: "hidden"}}>
                    {!selectedSermon 
                    ?
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress style={{ marginTop: "50px" }} />
                            <br />
                            <Typography variant="body1" color="textSecondary" style={{ textAlign: 'center' }}>
                                Loading Premium Sermon...
                            </Typography>
                        </div>
                    :
                        <div style={{ padding: '20px' }}>
                            <Card className={classes.card}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={12} md={5} lg={4} xl={3} style={{ padding: 0 }}>
                                        <img alt="Premium" height="100%" width="100%" src={selectedSermon.thumbnailUrl} onError={(e) => e.currentTarget.setAttribute('src', "//i.cdn-sc.com/Logos/SermonMaker_384x216.jpg")}/>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={7} lg={8} xl={9}>
                                        <CardContent>
                                            <Typography gutterBottom variant="h5" component="h2">
                                                {selectedSermon.name}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" component="p">
                                                {selectedSermon.description}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            {(user.makerSubscription || selectedSermon.isFree) ?
                                                <Button disabled={isDisabled} variant="contained" size="small" color="primary" onClick={activateSermon}>
                                                    Add to My Library
                                                </Button>
                                                :
                                                <Button disabled={isDisabled} variant="contained" size="small" color="primary" onClick={() => upsellToPaid(AddAssetAction.PremiumSermonSeries)}>
                                                    UNLOCK
                                                </Button>
                                            }
                                        </CardActions>
                                    </Grid>
                                </Grid>
                            </Card>
                            <PremiumSermonTable 
                                currentSermon={selectedSermon}
                                openPreview={(asset) => openPreview(asset.id)}
                            />
                        </div>
                    }
                    </Grid>
                    }
                </Grid>
                {
                    showPreview && previewAsset && 
                    <PreviewPremiumContentDialog 
                        showDialog={showPreview}
                        freeAsset={selectedSermon ? selectedSermon.isFree : false}
                        isDisabled={isDisabled}
                        currentPremiumAsset={previewAsset}
                        onClose={closePreview}
                        activatePremiumContent={activateSermon}
                        upSellSeries={() => upsellToPaid(AddAssetAction.PremiumSermonSeries)}
                    />
                }
            </div>
        );
    }